import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createQueuedFrameContentSave } from "@/components/shapes/shared/queued-frame-content-save";
import { translate } from "@/i18n";
import { todoFrameMessages } from "@/i18n/messages/frames-todo";
import { savedItemsWithIds } from "./todo-items";

type Content = Record<string, unknown>;
type SavedItem = Record<string, unknown>;

interface ProjectSave {
  /** The project the save writes to; undefined means the current project. */
  projectId: string | undefined;
  save: ReturnType<typeof createQueuedFrameContentSave>;
}

function projectSave(
  frameId: string,
  projectId: string | undefined,
  content: Content | undefined,
): ProjectSave {
  return {
    projectId,
    save: createQueuedFrameContentSave({
      frameId,
      initialContent: content ?? {},
      errorTitle: () => translate(todoFrameMessages, "saveError"),
      projectId,
    }),
  };
}

export interface TodoEdits {
  toggle: (itemId: string) => void;
  /** Sets an item's text. */
  setText: (itemId: string, text: string) => void;
  /**
   * Adds an empty item after `afterId` (at the end when null), in the same
   * save as `afterText`, the text `afterId` holds; returns the new item's id.
   */
  addAfter: (afterId: string | null, afterText?: string) => string;
  remove: (itemId: string) => void;
}

/**
 * Edits a checklist in place, for the opened checklist and its desktop
 * widget: the queued frame-content save (optimistic frames-store update +
 * serialized updateFrame) writes the items back. Agent and remote updates
 * reset the save's draft; our own edits never do (a reset would invalidate
 * the save in flight). A frame moved to another project (a pinned widget
 * outlives the move) saves there from then on.
 */
export function useTodoEdits({
  frameId,
  content,
  projectId,
}: {
  frameId: string;
  content: Content | undefined;
  /** The project it saves to; without it the current project. */
  projectId?: string;
}): TodoEdits {
  const [current, setCurrent] = useState(() =>
    projectSave(frameId, projectId, content),
  );
  if (current.projectId !== projectId) {
    // Derived state: replace the save during render, before any edit uses it.
    setCurrent(projectSave(frameId, projectId, content));
  }
  const { save } = current;
  // The last content object we wrote optimistically.
  const lastWrittenRef = useRef(content);

  useEffect(() => {
    if (content !== lastWrittenRef.current) {
      save.reset(content ?? {});
      lastWrittenRef.current = content;
    }
  }, [content, save]);

  const edit = useCallback(
    (change: (items: SavedItem[]) => SavedItem[]) => {
      save.update((draft) => {
        const updated = {
          ...draft,
          items: change(savedItemsWithIds(draft.items)),
        };
        lastWrittenRef.current = updated;
        return updated;
      });
    },
    [save],
  );

  return useMemo(
    () => ({
      toggle: (itemId) =>
        edit((items) =>
          items.map((item) =>
            item.id === itemId ? { ...item, done: item.done !== true } : item,
          ),
        ),
      setText: (itemId, text) =>
        edit((items) =>
          items.map((item) => (item.id === itemId ? { ...item, text } : item)),
        ),
      addAfter: (afterId, afterText) => {
        const added = { id: crypto.randomUUID(), text: "", done: false };
        edit((items) => {
          const next = items.map((item) =>
            item.id === afterId && afterText !== undefined
              ? { ...item, text: afterText }
              : item,
          );
          const at = next.findIndex((item) => item.id === afterId);
          next.splice(at === -1 ? next.length : at + 1, 0, added);
          return next;
        });
        return added.id;
      },
      remove: (itemId) =>
        edit((items) => items.filter((item) => item.id !== itemId)),
    }),
    [edit],
  );
}
